"use client";
// ----- Components Import -----
import React, { useState } from "react";
import { Row, Col } from "antd";
import styled from "styled-components";
import { useBreakpoints } from "../../lib/breakpoints";
import { Spacer, Spinner } from "../../styles/CommonElements";
import Button from "../../components/Button";
import { trackEvent } from "../../helpers/mixpanel";
import { APP_NAME } from "../../helpers/constants";
import Logo from "../../components/general/Logo";

export default function ContactPage() {
  const { isMobile } = useBreakpoints();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!name.trim() || !email.trim() || !message.trim()) {
      alert("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);

    try {
      trackEvent("Contact Form Submitted", {
        name,
        email,
        device_type: isMobile ? "mobile" : "desktop",
      });

      // Prepare the data payload - send empty strings for shopName and website
      const contactData = {
        name: name.trim(),
        email: email.trim(),
        shopName: "",
        website: "",
        message: message.trim(),
      };

      // Make API call to the contact form endpoint
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sendContactForm`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(contactData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();
      console.log("Contact form submitted successfully:", result);

      setSuccessfullySubmitted(true);

      trackEvent("Contact Form Submission Success", {
        name,
        email,
        device_type: isMobile ? "mobile" : "desktop",
      });

      // Reset form after success
      setTimeout(() => {
        setName("");
        setEmail("");
        setMessage("");
        setSuccessfullySubmitted(false);
      }, 7000);
    } catch (error) {
      console.error("Contact form submission error:", error);

      trackEvent("Contact Form Submission Error", {
        name,
        email,
        error_message: error.message,
        device_type: isMobile ? "mobile" : "desktop",
      });

      // Show user-friendly error message
      const errorMessage = error.message.includes("fetch")
        ? "Unable to connect to our servers. Please check your internet connection and try again."
        : "There was an error submitting your message. Please try again or contact us directly.";

      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      {/* Wave Pattern */}
      <WavePattern />

      <ContentWrapper>
        <Row justify="center" align="middle" style={{ minHeight: "100vh" }}>
          <Col xs={22} sm={20} md={16} lg={14} xl={12} style={{ zIndex: 2 }}>
            <FormCard>
              {!isSubmitting && successfullySubmitted && (
                <SuccessContainer>
                  <SuccessTitle>Thank you for your interest!</SuccessTitle>
                  <SuccessMessage>
                    Our team will reach out within 1 business day to answer your
                    questions and help you get started.
                  </SuccessMessage>
                </SuccessContainer>
              )}

              {isSubmitting && !successfullySubmitted && (
                <LoadingContainer>
                  <SpinnerContainer>
                    <Spinner />
                  </SpinnerContainer>
                  <LoadingText>Sending your message...</LoadingText>
                </LoadingContainer>
              )}

              {!isSubmitting && !successfullySubmitted && (
                <>
                  {/* LandlordOps Icon */}
                  <IconContainer>
                    <LandlordOpsIcon>
                      <Logo
                        color="white"
                        displayMode="icon"
                        size="medium"
                        linkTo={null}
                      />
                    </LandlordOpsIcon>
                  </IconContainer>

                  {/* Header Section */}
                  <HeaderSection>
                    <MainTitle>Contact Us</MainTitle>
                    <Subtitle>
                      Have questions about our property management platform?
                      Need help getting started? Our team is here to help you
                      streamline your operations.
                    </Subtitle>
                  </HeaderSection>

                  {/* Contact Form */}
                  <ContactForm onSubmit={handleSubmit}>
                    <FormRow gutter={[24, 24]}>
                      <FormCol xs={24} sm={12}>
                        <FormGroup>
                          <Label>
                            Full Name<RequiredAsterisk>*</RequiredAsterisk>
                          </Label>
                          <StyledInput
                            value={name}
                            type="text"
                            placeholder="Enter your full name"
                            maxLength={100}
                            onChange={(e) => setName(e.target.value)}
                            required
                          />
                        </FormGroup>
                      </FormCol>

                      <FormCol xs={24} sm={12}>
                        <FormGroup>
                          <Label>
                            Email Address<RequiredAsterisk>*</RequiredAsterisk>
                          </Label>
                          <StyledInput
                            value={email}
                            type="email"
                            placeholder="you@email.com"
                            maxLength={100}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                          />
                        </FormGroup>
                      </FormCol>

                      <FormCol xs={24}>
                        <FormGroup>
                          <Label>
                            Tell us about your needs
                            <RequiredAsterisk>*</RequiredAsterisk>
                          </Label>
                          <StyledTextArea
                            value={message}
                            placeholder="Tell us about your property management needs, questions about our platform, or any other inquiries you have."
                            rows={4}
                            maxLength={1000}
                            onChange={(e) => setMessage(e.target.value)}
                            required
                          />
                        </FormGroup>
                      </FormCol>
                    </FormRow>

                    <SubmitButtonContainer>
                      <SubmitButton type="submit">Send Message</SubmitButton>
                      <ResponseTimeText>
                        <em>We typically respond within 1 business day.</em>
                      </ResponseTimeText>
                    </SubmitButtonContainer>
                  </ContactForm>
                </>
              )}
            </FormCard>
          </Col>
        </Row>
      </ContentWrapper>
    </Container>
  );
}

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  // background: linear-gradient(100deg, var(--color-accent-dark) 0%, var(--color-accent-dark) 75%, var(--color-primary) 100%);
  position: relative;
  overflow: hidden;
  padding: 2rem 0;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 30% 20%,
      rgba(44, 95, 45, 0.03) 0%,
      transparent 50%
    );
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 1rem 0;
  }
`;

const WavePattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 200px;
  background: var(--gradient-primary);
  clip-path: polygon(0 0, 100% 0, 100% 70%, 0 100%);
  z-index: 1;

  @media (max-width: 768px) {
    height: 150px;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 2;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FormCard = styled.div`
  background: var(--color-bg-secondary);
  border-radius: 32px;
  padding: 4rem 3rem 3rem 3rem;
  box-shadow:
    0 20px 60px var(--shadow-light),
    0 8px 25px var(--shadow-green);
  border: 1px solid var(--color-border-light);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
  }

  @media (max-width: 768px) {
    padding: 3rem 2rem 2rem 2rem;
    border-radius: 24px;
  }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 2rem;
`;

const LandlordOpsIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: var(--gradient-primary);
  border-radius: 50%;
  box-shadow: 0 8px 25px var(--shadow-green);
  transition: none;
  pointer-events: none;

  img {
    width: 44px;
    height: 44px;
    object-fit: contain;
    transition: none;
  }

  &:hover,
  &:active,
  &:focus {
    background: var(--gradient-primary);
    box-shadow: 0 8px 25px var(--shadow-green);
  }

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    img {
      width: 32px;
      height: 32px;
    }
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const MainTitle = styled.h1`
  font-family: var(--font-primary);
  font-size: 3.25rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 1.5rem;
  line-height: 1.2;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 2.75rem;
    margin-bottom: 1rem;
  }
`;

const Subtitle = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.25rem;
  color: var(--color-text-light);
  line-height: 1.6;
  max-width: 500px;
  margin: 0 auto;
  letter-spacing: -0.005em;

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const ContactForm = styled.form`
  width: 100%;
`;

const FormRow = styled(Row)`
  margin-bottom: 1rem;
`;

const FormCol = styled(Col)`
  margin-bottom: 1rem;
  vertical-align: top;
`;

const FormGroup = styled.div`
  width: 100%;
  min-height: 92px;
`;

const Label = styled.label`
  display: block;
  font-family: var(--font-secondary);
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.75rem;
  min-height: 24px;
  line-height: 1.2;
  letter-spacing: -0.005em;
`;

const RequiredAsterisk = styled.span`
  color: var(--color-primary);
  margin-left: 2px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 1rem 1.5rem;
  border: 2px solid var(--color-border-light);
  border-radius: 16px;
  font-family: var(--font-secondary);
  font-size: 1.125rem;
  color: var(--color-text);
  background: var(--color-bg-secondary);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px var(--shadow-light);
  margin-bottom: 0;
  vertical-align: top;

  &::placeholder {
    color: var(--color-text-muted);
    font-weight: 400;
  }

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 4px var(--shadow-light);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: var(--color-border-medium);
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    font-size: 1rem;
  }
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  padding: 1.25rem 1.5rem;
  border: 2px solid var(--color-border-light);
  border-radius: 20px;
  font-family: var(--font-secondary);
  font-size: 1.125rem;
  color: var(--color-text);
  background: var(--color-bg-secondary);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px var(--shadow-light);
  resize: vertical;
  min-height: 140px;

  &::placeholder {
    color: var(--color-text-muted);
    font-weight: 400;
  }

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 4px var(--shadow-light);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: var(--color-border-medium);
  }

  @media (max-width: 768px) {
    padding: 1rem 1rem;
    font-size: 1rem;
  }
`;

const SubmitButtonContainer = styled.div`
  text-align: center;
  margin-top: 2rem;
`;

const SubmitButton = styled.button`
  background: var(--gradient-primary);
  color: var(--color-text-white);
  border: none;
  border-radius: 50px;
  padding: 1.25rem 3rem;
  font-family: var(--font-secondary);
  font-size: 1.25rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 25px var(--shadow-green);
  min-width: 240px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover {
    background: var(--gradient-button-hover);
    transform: scale(1.05);
    box-shadow: 0 12px 35px var(--shadow-dark);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem 2rem;
    font-size: 1.125rem;
    min-width: auto;
  }
`;

const ResponseTimeText = styled.p`
  font-family: var(--font-secondary);
  font-size: 1rem;
  color: var(--color-primary);
  margin-top: 1rem;
  margin-bottom: 0;
  font-style: italic;
  font-weight: 500;
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: linear-gradient(
    135deg,
    rgba(44, 95, 45, 0.08) 0%,
    rgba(44, 95, 45, 0.03) 100%
  );
  border-radius: 24px;
  border: 1px solid var(--color-border-green);
  box-shadow: 0 8px 32px var(--shadow-light);
  position: relative;
  overflow: hidden;

  /* Removed the gold accent top border */

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 200px;
    height: 200px;
    background: radial-gradient(
      circle,
      rgba(44, 95, 45, 0.05) 0%,
      transparent 70%
    );
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  filter: drop-shadow(0 4px 12px var(--shadow-light));
  animation: successPulse 2s ease-in-out infinite;

  @keyframes successPulse {
    0%,
    100% {
      transform: scale(1);
      filter: drop-shadow(0 4px 12px var(--shadow-light));
    }
    50% {
      transform: scale(1.1);
      filter: drop-shadow(0 6px 20px var(--shadow-medium));
    }
  }

  @media (max-width: 768px) {
    font-size: 3rem;
    margin-bottom: 1rem;
  }
`;

const SuccessTitle = styled.h2`
  font-family: var(--font-primary);
  font-size: 2rem;
  font-weight: 600;
  color: var(--color-primary);
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;
  text-shadow: 0 2px 8px var(--shadow-light);

  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 1rem;
  }
`;

const SuccessMessage = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.25rem;
  color: var(--color-text);
  line-height: 1.7;
  max-width: 450px;
  margin: 0 auto;
  font-weight: 500;
  letter-spacing: -0.005em;

  @media (max-width: 768px) {
    font-size: 1.125rem;
    max-width: 100%;
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 3rem 1rem;
`;

const LoadingText = styled.p`
  font-family: var(--font-secondary);
  font-size: 1.125rem;
  color: var(--color-text-light);
  margin-top: 1rem;
  margin-bottom: 0;
`;

const SpinnerContainer = styled.div`
  margin: auto;
  text-align: center;
  display: inline-flex;
`;
